<template>
  <v-container class="general-overview-container standard-content-container">
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link
              :to="{ name: 'OverviewPage', hash: '#referrals' }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="standard-title-container">
            <h1 class="">Referrals</h1>
            <h3>Help others to protect their legacy</h3>
            <div class="mt-4 mb-6 refer-friend-image">
              <v-img :src="whitelabel.images.referAFriendImage" />
            </div>
            <p class="your-people-description">
              Your referral is more than a suggestion—it’s a way to support the
              people you care about during life’s most important decisions.
              Encourage them to start their journey to peace of mind today with
              Old Mutual Will.
            </p>
            <div class="mt-8">
              <TextField
                v-model.trim="testator.referralUrl"
                :label="'My referral link'"
                :readonly="true"
                :name="'referral-url'"
              />
            </div>
            <div class="mb-8">
              <GradientButton
                :title="'Copy unique referral link'"
                :small="true"
                @gradientButtonClicked="copyReferralLink"
              />
            </div>
            <h3>Quick links:</h3>
            <div>
              <a
                @click.prevent="scrollToSection('balance-of-referrals')"
                href="#balance-of-referrals"
                class="referral-link"
                >Balance of referrals</a
              >
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <h3>Select an existing person or add new person</h3>
          <div v-if="!loading">
            <div class="list-section">
              <AllPeopleList
                v-if="testatorPeople"
                :people="testatorPeople"
                :testator="testator"
                @peopleSelected="peopleSelected"
                relation="REFERRAL"
              />
            </div>

            <h3 class="add-message-to-referral-title">
              You can add a message to your referrals
            </h3>

            <ValidationObserver ref="observer">
              <ValidationProvider
                id="email-message"
                v-slot="{ errors }"
                name="email-message"
              >
                <TextField
                  v-model.trim="emailMessage"
                  :label="'Message (optional)'"
                  :name="'email-message'"
                  :type="'textarea'"
                />
              </ValidationProvider>
            </ValidationObserver>
            <v-row no-gutters>
              <div class="button-container">
                <GradientButton
                  :title="'Send Email(s)'"
                  :small="true"
                  :callout="'right'"
                  :disabled="this.selectedPeopleToEmail.length === 0"
                  @gradientButtonClicked="sendEmails"
                />
              </div>
            </v-row>
          </div>

          <div v-else class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </div>
      </v-col>
    </v-row>
    <hr class="section-separator" />
    <v-row no-gutters>
      <v-col cols="12" md="5"> </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <h3 id="balance-of-referrals">Balance of referrals</h3>
          <div v-if="!loading">
            <div class="list-section">
              <ReferralsList
                v-if="referralPeople"
                :people="referralPeople"
                :testator="testator"
                relation="REFERRAL"
              />
            </div>
          </div>

          <div v-else class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
        </div>
      </v-col>
    </v-row>

    <SuccessSnackbar v-model="showSuccessToast" :message="successMessage" />
  </v-container>
</template>

<script>
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { CREATE_TESTATOR_RELATIONSHIP_MUTATION } from '../../constants/graphql/people';
  import { EMAIL_TESTATOR_REFERRALS } from '../../constants/graphql/referrals';
  import AllPeopleList from './AllPeopleList.vue';
  import ReferralsList from './ReferralsList.vue';
  import HR from '../common/HR.vue';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';

  export default {
    name: 'Referrals',
    components: {
      AllPeopleList,
      ReferralsList,
      HR,
      SuccessSnackbar,
    },
    data() {
      return {
        testator: {},
        relations: {},
        loading: false,
        emailMessage: '',
        selectedPeopleToEmail: [],
        showSuccessToast: false,
        successMessage: '',
      };
    },
    computed: {
      testatorPeople() {
        return (
          this.testator?.people?.filter(person => {
            if (person.kinship === 'CHILD') {
              const age = this.calculateAge(person.dateOfBirth);
              if (age < 18) {
                return false;
              }
            }

            return person.testatorRelationships.every(relationship => {
              return (
                relationship.relation !== 'REFERRAL' ||
                !person.referralInviteSent
              );
            });
          }) || []
        );
      },
      referralPeople() {
        return (
          this.testator?.people?.filter(person => {
            return person.testatorRelationships.some(relationship => {
              return (
                relationship.relation === 'REFERRAL' &&
                person.referralInviteSent
              );
            });
          }) || []
        );
      },
    },
    methods: {
      scrollToSection(id) {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      },
      peopleSelected(people) {
        this.selectedPeopleToEmail = people;
      },
      referralRelationshipBlank(person) {
        return (
          person.testatorRelationships.filter(relationship => {
            return relationship.relation === 'REFERRAL';
          }).length == 0
        );
      },
      sendEmails() {
        this.selectedPeopleToEmail.forEach(person => {
          if (this.referralRelationshipBlank(person)) {
            this.createTestatorRelationship(person);
          }
        });

        this.sendBatchEmails();
        dataLayer.push({
          event: 'referral_submitted',
          omuserid: this.$store.getters?.userId,
          omtid: this.$store.getters.omtId,
        });
      },
      sendBatchEmails() {
        const peopleIds = this.selectedPeopleToEmail.map(person => {
          return parseInt(person.id);
        });

        this.$apollo
          .mutate({
            mutation: EMAIL_TESTATOR_REFERRALS,
            variables: {
              userId: parseInt(this.userId),
              peopleIds: peopleIds,
              message: this.emailMessage,
            },
            update: (cache, { data: { emailTestatorReferrals } }) => {
              this.updateCacheAfterReferralsSent(cache, emailTestatorReferrals);
            },
          })
          .then(_response => {
            this.emailMessage = '';
          })
          .catch(error => {
            throw error;
          });
      },
      createTestatorRelationship(person) {
        const testatorRelationshipInputDetails = {
          relation: 'REFERRAL',
          personId: person.id,
        };
        let context = this;

        this.$apollo
          .mutate({
            mutation: CREATE_TESTATOR_RELATIONSHIP_MUTATION,
            variables: {
              testatorRelationshipInputDetails,
            },
            update: (cache, { data: { createTestatorRelationship } }) => {
              context.updateCacheAfterCreateTestatorRelationship(
                cache,
                createTestatorRelationship
              );
            },
          })
          .then(_response => {})
          .catch(error => {
            throw error;
          });
      },
      updateCacheAfterCreateTestatorRelationship(
        cache,
        createTestatorRelationship
      ) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var person = data.testator.people.find(
          p => p.id == createTestatorRelationship.personId
        );
        person.testatorRelationships.push(createTestatorRelationship);

        cache.writeQuery({ ...query, data: data });
      },
      updateCacheAfterReferralsSent(cache, emailTestatorReferrals) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        emailTestatorReferrals.forEach(dbPerson => {
          const personIndex = data.testator.people.findIndex(
            p => p.id === dbPerson.id
          );

          if (personIndex !== -1) {
            data.testator.people[personIndex] = dbPerson;
          }
        });
        cache.writeQuery({ ...query, data: data });
      },
      copyReferralLink() {
        navigator.clipboard.writeText(this.testator.referralUrl);
        this.showSuccessToast = true;
        this.successMessage = 'Referral link copied to clipboard!';

        setTimeout(() => {
          this.showSuccessToast = false;
        }, 3000);
      },
      calculateAge(dateOfBirth) {
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }
        return age;
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        result(response) {
          if (response.data) {
            this.loading = false;
          }
        },
      },
    },
  };
</script>

<style scoped lang="scss">
  .general-overview-container {
    text-align: left;
    padding-top: 40px;

    .section-separator {
      border: 1px solid #dfe2e5;
      margin-bottom: 60px;
    }
    .section-separator.bottom {
      margin-top: 60px;
    }

    .standard-title-container {
      h3 {
        margin-bottom: 17px;
      }
    }

    h3 {
      font-size: 24px;
      font-weight: 600;
      line-height: 31.2px;
      margin-bottom: 30px;
    }

    .referral-link {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      text-decoration: underline;
      color: var(--v-font-color-base);
    }

    .add-message-to-referral-title {
      margin-top: 30px;
      margin-bottom: 24px;
      font-size: 16px;
      font-weight: 700;
      line-height: 30px;
      color: #282828;
    }
    .button-container {
      margin-top: 50px;
    }
  }
</style>
